import React, { useEffect, useState } from "react";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { Download } from "lucide-react";

const COLORS = ["#2E6675", "#3949AB", "#183853"];

const ActiveUserByPlatformChart = ({
  extdata,
  chartFlag,
  daysSelected,
  handleCSVDowload,
  donwlodingState,
}) => {
  const [chartData, setChartData] = useState([]);

  useEffect(() => {
    if (extdata && extdata?.data && chartFlag) {
      prepareData();
    }
  }, [extdata, chartFlag, daysSelected]);

  const prepareData = () => {
    const d = extdata?.data;

    if (!d) {
      setChartData([
        { name: "iOS", value: 0, percentage: 0 },
        { name: "Android", value: 0, percentage: 0 },
        { name: "Web", value: 0, percentage: 0 },
      ]);
      return;
    }

    let ios = 0,
      android = 0,
      web = 0;

    if (chartFlag === "Job") {
      const jdata = d[`last${daysSelected}Days`];
      if (!jdata) {
        setChartData([
          { name: "iOS", value: 0, percentage: 0 },
          { name: "Android", value: 0, percentage: 0 },
          { name: "Web", value: 0, percentage: 0 },
        ]);
        return;
      }
      ios = (jdata?.bpo?.ios || 0) + (jdata?.dh?.ios || 0);
      android = (jdata?.bpo?.android || 0) + (jdata?.dh?.android || 0);
      web = (jdata?.bpo?.web || 0) + (jdata?.dh?.web || 0);
    } else {
      ios = d?.ios ?? 0;
      android = d?.android ?? 0;
      web = d?.web ?? 0;
    }

    const total = ios + android + web;

    // 🛠 Avoid divide-by-zero & ensure chart renders
    const formattedData =
      total === 0
        ? [
          { name: "iOS", value: 0, percentage: 0 },
          { name: "Android", value: 0, percentage: 0 },
          { name: "Web", value: 0, percentage: 0 },
        ]
        : [
          {
            name: "iOS",
            value: ios,
            percentage: ((ios / total) * 100).toFixed(1),
          },
          {
            name: "Android",
            value: android,
            percentage: ((android / total) * 100).toFixed(1),
          },
          {
            name: "Web",
            value: web,
            percentage: ((web / total) * 100).toFixed(1),
          },
        ];

    setChartData(formattedData);
  };

  const allZero = chartData.every((item) => item.value === 0);

  return (
    <div className="">
      <div className=" border-gray-200 border rounded-lg  bg-white shadow-sm">
        {/* Header */}
        <div className="flex flex-row items-center justify-between p-4">
          <div>
            <h2 className="text-lg font-semibold text-[#183853]">
              Active Users by Platform
            </h2>
            <p className="text-sm text-gray-500">
              Distribution across iOS, Android, and Web
            </p>
          </div>
          <button
            onClick={handleCSVDowload}
            disabled={donwlodingState?.activeUserloading}
            className="flex items-center border bg-white border-gray-300 text-gray-700 hover:bg-gray-100 px-3 py-1 rounded-md text-sm"
          >
            {donwlodingState?.activeUserloading ? (
              <span>Downloading...</span>
            ) : (
              <>
                <Download className="mr-2 h-4 w-4" />
                Download
              </>
            )}
          </button>
        </div>

        {/* Content */}
        <div className="p-6 flex flex-col items-center">
          <div className="w-full h-[300px] flex items-center justify-center">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart style={{ pointerEvents: "none" }}>
                <Pie
                  data={allZero ? [{ name: "No Data", value: 1 }] : chartData}
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  dataKey="value"
                  labelLine={false}
                  label={({ name, percentage, value }) => {
                    if (allZero) return "No Data";
                    if (value === 0) return ""; // hide label for zero slices
                    return `${name}: ${percentage}%`;
                  }}
                >
                  {(allZero ? [0] : chartData).map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={allZero ? "#E0E0E0" : COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>

                <Tooltip
                  formatter={(value, name) =>
                    chartData.some((d) => d.value > 0)
                      ? [`${value}`, name]
                      : ["0", name]
                  }
                />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Legend */}
          {/* Legend */}
          <div className="mt-4 space-y-2 w-full">
            {chartData.map((item, index) => (
              <div key={item.name} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: COLORS[index] }}
                  />
                  <span className="text-sm font-medium">{item.name}</span>
                </div>
                <span className="text-sm text-gray-500">
                  {item.value > 0
                    ? `${item.value.toLocaleString()} (${item.percentage}%)`
                    : `0 (0%)`}
                </span>
              </div>
            ))}
          </div>

        </div>
      </div>
    </div>
  );
};

export default ActiveUserByPlatformChart;
